import { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Checkbox from "@mui/material/Checkbox";
import TextField from "@mui/material/TextField";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import api from "api/axios";

const typeLabel = { double: "Double", triple: "Triple", quadruple: "Quadruple", sans_type: "Accompagnateurs / sans type" };
const typeColor = { double: "#1A73E8", triple: "#7c3aed", quadruple: "#fb8c00", sans_type: "#6a1b9a" };

function PlacementChambres({ sessionId, onChambresChange }) {
  const [chambres, setChambres] = useState([]);
  const [nonPlaces, setNonPlaces] = useState([]);
  const [selection, setSelection] = useState(null); // participant sélectionné (clic)
  const [dragge, setDragge] = useState(null); // participant en cours de glisser
  const [loading, setLoading] = useState(false);

  const charger = async () => {
    if (!sessionId) return;
    setLoading(true);
    try {
      const res = await api.get("/oumrah/hebergement/" + sessionId + "/chambres");
      setChambres(res.data.chambres || []);
      setNonPlaces(res.data.non_places || []);
    } catch (err) {
      // silencieux
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { charger(); }, [sessionId]);

  const nom = (p) => (p.prenom || "") + " " + (p.nom ? p.nom.toUpperCase() : "");

  const placer = async (participantId, chambreId) => {
    try {
      await api.put("/oumrah/hebergement/placer", { participant_id: participantId, chambre_id: chambreId });
      setSelection(null);
      charger();
    } catch (err) {
      alert("Erreur lors du placement");
    }
  };

  const genererChambres = async () => {
    if (!window.confirm("Générer les chambres selon le calcul ? Les chambres existantes seront conservées.")) return;
    try {
      await api.post("/oumrah/hebergement/" + sessionId + "/chambres/generer");
      charger();
      if (onChambresChange) onChambresChange();
    } catch (err) {
      alert("Erreur lors de la génération");
    }
  };

  const placerAuto = async () => {
    if (!window.confirm("Placer automatiquement les personnes non placées ? Les placements actuels seront conservés.")) return;
    try {
      const res = await api.post("/oumrah/hebergement/" + sessionId + "/placer-auto");
      charger();
      alert(res.data.message || "Placement effectué");
    } catch (err) {
      alert("Erreur lors du placement automatique");
    }
  };

  const ajouterChambre = async () => {
    const type = window.prompt("Type de chambre ? (double / triple / quadruple)", "double");
    if (!type || !["double", "triple", "quadruple"].includes(type)) return;
    const nomCh = window.prompt("Nom de la chambre ?", typeLabel[type] + " manuelle");
    if (!nomCh) return;
    try {
      await api.post("/oumrah/hebergement/" + sessionId + "/chambres", { nom: nomCh, type_chambre: type });
      charger();
      if (onChambresChange) onChambresChange();
    } catch (err) {
      alert("Erreur");
    }
  };

  const renommerChambre = async (id, nouveauNom) => {
    if (!nouveauNom || !nouveauNom.trim()) return;
    try {
      await api.put("/oumrah/hebergement/chambres/" + id, { nom: nouveauNom.trim() });
      charger();
    } catch (err) {
      alert("Erreur lors du renommage");
    }
  };
  const toggleLitAppoint = async (participantId, valeur) => {
    try {
      await api.put("/oumrah/hebergement/lit-appoint", { participant_id: participantId, lit_appoint: valeur });
      charger();
    } catch (err) {
      alert("Erreur lors de la mise à jour du lit d'appoint");
    }
  };
  const supprimerChambre = async (id) => {
    if (!window.confirm("Supprimer cette chambre ? Les occupants redeviendront non placés.")) return;
    try {
      await api.delete("/oumrah/hebergement/chambres/" + id);
      charger();
      if (onChambresChange) onChambresChange();
    } catch (err) {
      alert("Erreur");
    }
  };

  // Avertissements souples sur une chambre
  const alertesChambre = (ch) => {
    const alertes = [];
    if (ch.occupants.length > ch.capacite) {
      const surplus = ch.occupants.length - ch.capacite;
      const aLitAppoint = ch.occupants.some((o) => o.lit_appoint);
      if (surplus === 1 && aLitAppoint) {
        // Dépassement volontaire via lit d'appoint : message info, pas alerte
        alertes.push({ texte: "Lit d'appoint ajouté (" + ch.occupants.length + "/" + ch.capacite + ")", type: "info" });
      } else {
        alertes.push({ texte: "Capacité dépassée (" + ch.occupants.length + "/" + ch.capacite + ")", type: "alerte" });
      }
    }
    // Mixité : tolérée si les occupants sont un couple marié OU tous de la même famille (même inscription).
    const sexes = new Set(ch.occupants.map((o) => o.sexe));
    if (sexes.size > 1) {
      // Tous de la même inscription (famille) ?
      const inscriptions = new Set(ch.occupants.map((o) => o.inscription_id));
      const memeFamille = inscriptions.size === 1 && ch.occupants[0].inscription_id != null;
      // Couple marié (exactement 2 occupants, conjoints réciproques) ?
      let couple = false;
      if (ch.occupants.length === 2) {
        const [a, b] = ch.occupants;
        couple = a.conjoint_id === b.id && b.conjoint_id === a.id;
      }
      if (!memeFamille && !couple) {
        alertes.push({ texte: "Mixité non conforme", type: "alerte" });
      }
    }
    return alertes;
  };

  // Non-placés groupés par type de chambre
  const nonPlacesParType = { double: [], triple: [], quadruple: [], sans_type: [] };
  nonPlaces.forEach((p) => {
    if (p.type_chambre && nonPlacesParType[p.type_chambre]) nonPlacesParType[p.type_chambre].push(p);
    else nonPlacesParType.sans_type.push(p);
  });

  const onDragStart = (p) => setDragge(p);
  const onDragEnd = () => setDragge(null);
  const onDropChambre = (chambreId) => {
    if (dragge) { placer(dragge.id, chambreId); setDragge(null); }
  };
  const onDropNonPlaces = () => {
    if (dragge) { placer(dragge.id, null); setDragge(null); }
  };

  const telechargerPdf = async () => {
    try {
      const res = await api.get("/oumrah/hebergement/" + sessionId + "/placement/pdf", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "placement-chambres.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert("Erreur lors de la génération du PDF");
    }
  };

  if (!sessionId) return null;

  return (
    <MDBox mt={3}>
      <Card sx={{ p: 3 }}>
        <MDBox display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} mb={2}>
          <MDTypography variant="h5">Placement dans les chambres</MDTypography>
          <MDBox display="flex" gap={1}>
            <MDButton size="small" color="info" onClick={genererChambres}>
              <Icon>auto_awesome</Icon>&nbsp;Générer les chambres
            </MDButton>
            <MDButton size="small" color="success" onClick={placerAuto}>
              <Icon>shuffle</Icon>&nbsp;Placer automatiquement
            </MDButton>
            <MDButton size="small" color="secondary" variant="outlined" onClick={ajouterChambre}>
              <Icon>add</Icon>&nbsp;Ajouter une chambre
            </MDButton>
            <MDButton size="small" color="dark" variant="outlined" onClick={telechargerPdf} disabled={chambres.length === 0}>
              <Icon>picture_as_pdf</Icon>&nbsp;PDF
            </MDButton>
          </MDBox>
        </MDBox>

        {selection && (
          <MDBox mb={2} p={1.5} sx={{ bgcolor: "#e3f2fd", borderRadius: "8px" }}>
            <MDTypography variant="button" color="info">
              {nom(selection)} sélectionné(e) — cliquez une chambre pour l'y placer, ou
            </MDTypography>
            <MDButton size="small" color="error" variant="text" onClick={() => setSelection(null)}>Annuler</MDButton>
          </MDBox>
        )}

        <Grid container spacing={2}>
          {/* Colonne non-placés */}
          <Grid item xs={12} md={3}>
            <Card
              onDragOver={(e) => e.preventDefault()}
              onDrop={onDropNonPlaces}
              sx={{ p: 2, bgcolor: "#fafafa", height: "100%" }}>
              <MDTypography variant="button" fontWeight="bold" display="block" mb={1}>
                Non placés ({nonPlaces.length})
              </MDTypography>
              {["double", "triple", "quadruple", "sans_type"].map((type) => (
                nonPlacesParType[type].length > 0 && (
                  <MDBox key={type} mb={1.5}>
                    <MDTypography variant="caption" fontWeight="bold" sx={{ color: typeColor[type] }}>
                      {typeLabel[type]}
                    </MDTypography>
                    {nonPlacesParType[type].map((p) => (
                      <MDBox key={p.id}
                        draggable
                        onDragStart={() => onDragStart(p)}
                        onDragEnd={onDragEnd}
                        onClick={() => setSelection(p)}
                        sx={{
                          p: 0.8, mt: 0.5, borderRadius: "6px", cursor: "pointer",
                          border: "1px solid", borderColor: selection?.id === p.id ? "#1A73E8" : "#e0e0e0",
                          bgcolor: selection?.id === p.id ? "#e3f2fd" : "white",
                          display: "flex", alignItems: "center", gap: 0.5,
                        }}>
                        <Icon fontSize="small" sx={{ color: p.sexe === "H" ? "#1565c0" : "#c2185b" }}>
                          {p.sexe === "H" ? "man" : "woman"}
                        </Icon>
                        <MDTypography variant="caption">{nom(p)}</MDTypography>
                      </MDBox>
                    ))}
                  </MDBox>
                )
              ))}
              {nonPlaces.length === 0 && (
                <MDTypography variant="caption" color="text">Tout le monde est placé.</MDTypography>
              )}
            </Card>
          </Grid>

          {/* Grille des chambres */}
          <Grid item xs={12} md={9}>
            <Grid container spacing={2}>
              {chambres.map((ch) => {
                const alertes = alertesChambre(ch);
                return (
                  <Grid item xs={12} sm={6} lg={4} key={ch.id}>
                    <Card
                      onClick={() => { if (selection) placer(selection.id, ch.id); }}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={() => onDropChambre(ch.id)}
                      sx={{
                        p: 2, height: "100%", cursor: selection ? "pointer" : "default",
                        border: "2px solid", borderColor: alertes.some((a) => a.type === "alerte") ? "#e67e22" : typeColor[ch.type_chambre],
                        "&:hover": selection ? { boxShadow: 4 } : {},
                      }}>
                      <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <MDBox display="flex" alignItems="center" gap={0.5}>
                          <MDTypography variant="button" fontWeight="bold">{ch.nom}</MDTypography>
                          <IconButton size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              const nouveau = window.prompt("Nouveau nom de la chambre :", ch.nom);
                              if (nouveau !== null) renommerChambre(ch.id, nouveau);
                            }}
                            title="Renommer">
                            <Icon fontSize="small" sx={{ fontSize: "0.95rem !important", color: "#378ADD" }}>edit</Icon>
                          </IconButton>
                        </MDBox>
                        <IconButton size="small" color="error"
                          onClick={(e) => { e.stopPropagation(); supprimerChambre(ch.id); }}>
                          <Icon fontSize="small">delete</Icon>
                        </IconButton>
                      </MDBox>
                      <MDTypography variant="caption" color="text" display="block" mb={0.5}>
                        {typeLabel[ch.type_chambre]} · {ch.occupants.length}/{ch.capacite}
                      </MDTypography>
                      {ch.occupants.map((o) => (
                        <MDBox key={o.id}
                          draggable
                          onDragStart={(e) => { e.stopPropagation(); onDragStart(o); }}
                          onDragEnd={onDragEnd}
                          sx={{ p: 0.5, mb: 0.5, borderRadius: "4px", bgcolor: "#f5f5f5", display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "grab" }}>
                          <MDBox display="flex" alignItems="center" gap={0.5}>
                            <Icon fontSize="small" sx={{ color: o.sexe === "H" ? "#1565c0" : "#c2185b" }}>
                              {o.sexe === "H" ? "man" : "woman"}
                            </Icon>
                            <MDTypography variant="caption" sx={{ color: o.lit_appoint ? "#6a1b9a" : "inherit", fontWeight: o.lit_appoint ? "bold" : "regular" }}>
                              {nom(o)}
                            </MDTypography>
                          </MDBox>
                          <MDBox display="flex" alignItems="center">
                            <IconButton size="small"
                              onClick={(e) => { e.stopPropagation(); toggleLitAppoint(o.id, !o.lit_appoint); }}
                              title={o.lit_appoint ? "Retirer le lit d'appoint" : "Mettre sur lit d'appoint"}>
                              <Icon fontSize="small" sx={{ fontSize: "1rem !important", color: o.lit_appoint ? "#6a1b9a" : "#bdbdbd" }}>hotel</Icon>
                            </IconButton>
                            <IconButton size="small"
                              onClick={(e) => { e.stopPropagation(); placer(o.id, null); }}>
                              <Icon fontSize="small">close</Icon>
                            </IconButton>
                          </MDBox>
                        </MDBox>
                      ))}
                      {alertes.map((a, idx) => {
                        const couleur = a.type === "info" ? "#6a1b9a" : "#e67e22";
                        const icone = a.type === "info" ? "info" : "warning";
                        return (
                          <MDBox key={idx} display="flex" alignItems="center" gap={0.5} mt={0.5}>
                            <Icon fontSize="small" sx={{ color: couleur }}>{icone}</Icon>
                            <MDTypography variant="caption" sx={{ color: couleur }}>{a.texte}</MDTypography>
                          </MDBox>
                        );
                      })}
                    </Card>
                  </Grid>
                );
              })}
              {chambres.length === 0 && !loading && (
                <Grid item xs={12}>
                  <MDTypography variant="caption" color="text">
                    Aucune chambre. Cliquez « Générer les chambres » ou « Ajouter une chambre ».
                  </MDTypography>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Card>
    </MDBox>
  );
}

export default PlacementChambres;
